import EventBus from '@mvps-genesis/event-bus';
import {BopisController} from 'custom-extensions/bopis';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import styles from './LocationCard.component.scss?as=stylesheet';

export default class LocationCard extends LitElement {

	/**
	 * Define stylesheets
	 * @see https://lit.dev/docs/components/styles/
	 * @static
	 */
	static get styles () {
		return [styles];
	}

	/**
	 * Define reflector properties
	 * @see https://lit.dev/docs/components/properties/
	 * @static
	 */
	static get properties () {
		return {
			basketBopisStore: {
				type: Object
			},
			basketgroup: {
				type: String
			},
			excludeBopis: {
				type: Boolean
			},
			inStoreOnly: {
				type: Boolean
			},
			showDetails: {
				type: Boolean
			},
			showRadius: {
				attribute: 'show-radius',
				type: Boolean
			},
			store: {
				type: Object
			},
			titleCard: {
				type: Boolean
			},
			type: {
				type: String
			}
		};
	};

	/**
	 * @constructor
	 */
	constructor () {
		super();

		this.controller = BopisController;
		this.basketBopisStore = {};
		this.basketgroup = '';
		this.excludeBopis = false;
		this.store = {};
		this.showDetails = false;
		this.showRadius = false;
		this.titleCard = false;
		this.type = '';
		this.uniqueId = Math.floor(Math.random() * 100);
		this.hasFlags = false;
	}

	/**
	 * Invoked when a component is added to the document's DOM.
	 * @see https://lit.dev/docs/components/lifecycle/#connectedcallback
	 * @static
	 */
	connectedCallback () {
		super.connectedCallback();
		this.bopisStoreUnsubscribe = this.controller.store.subscribe(() => this.requestUpdate());
	}

	/**
	 * Invoked when a component is removed from the document's DOM.
	 * @see https://lit.dev/docs/components/lifecycle/#disconnectedcallback
	 * @static
	 */
	disconnectedCallback () {
		this.bopisStoreUnsubscribe();
		super.disconnectedCallback();
	}

	get isMyStore () {
		return this.store.identifier?.toString() === this.controller?.store?.state?.myStore?.identifier?.toString();
	}

	get isCurrentlySelected () {
		return this.store.identifier?.toString() === this.basketBopisStore?.identifier?.toString();
	}

	/**
	 * @see https://lit.dev/docs/components/rendering/
	 */
	render () {
		if (!this.store.identifier) {
			return nothing;
		}

		if (this.isMyStore) {
			return this.renderMyStore();
		}

		const dynamicClasses = {
			'location-card': true,
			'location-card--show-details': this.showDetails
		};

		return html`
			<div class="${classMap(dynamicClasses)}" data-identifier="${this.store.identifier}">
				${this.renderFlags()}
				<div class="location-card__inner">
					<div class="location-card__left">
						<div class="location-card__name">${this.store.name}</div>
						<div class="location-card__address">${this.formattedAddress(false)}</div>
					</div>
					<div class="location-card__right">
						${this.hasFlags ? nothing : this.drawProductInventoryFlag()}
						<div class="location-card__distance">${this.getDistance()}</div>
					</div>
				</div>
				<div class="location-card__subfooter">${this.drawSubfooter()}</div>
				${this.drawFooter()}
				${this.drawLocationDetails()}
			</div>
		`;
	}

	renderMyStore () {
		const dynamicClasses = {
			'location-card': true,
			'location-card--currently-shopping': true,
			'location-card--show-details': this.showDetails
		};

		return html`
			<div class="${classMap(dynamicClasses)}" data-identifier="${this.store.identifier}">
				${this.renderFlags()}
				<div class="location-card__inner">
					<div class="location-card__left">
						<div class="location-card__name">${this.store.name}</div>
						<div class="location-card__address">${this.formattedAddress(true)}</div>
						<div class="location-card__todays-hours">
							<button class="location-card__details-button g-button--no-styling" @click="${this.toggleDetails}" aria-expanded="${this.showDetails}" aria-controls="location-card-details-${this.store.identifier}">
								<span class="location-card__details-button-text">${this.todaysHours()}</span>
								<span class="location-card__details-button-icon u-icon-chevron-down" aria-hidden="true"></span>
							</button>
						</div>
					</div>
					<div class="location-card__right">
						${this.hasFlags ? nothing : this.drawProductInventoryFlag()}
						<div class="location-card__distance">${this.getDistance()}</div>
					</div>
				</div>
				${this.drawFooter()}
				${this.isProductOrBasket && !this.isCurrentlySelected ? html`<div class="location-card__currently-shopping-ctas">${this.drawShopStoreProduct()}</div>` : nothing}
				${this.drawLocationDetails()}
			</div>
		`;
	}

	renderFlags () {
		let elements = [];

		if (this.isMyStore) {
			elements.push(html`<x-my-store-flag identifier="${this.store.identifier}"></x-my-store-flag>`);
		}

		if (this.isProductOrBasket) {
			if (!this.isMyStore && this.isCurrentlySelected) {
				elements.push(this.drawCurrentlySelectedFlag());
			}

			if (elements.length) {
				elements.push(this.drawProductInventoryFlag());
			}
		}

		this.hasFlags = elements.length > 0;

		if (elements.length) {
			elements = html`<div class="location-card__flags">${elements}</div>`;
		}

		return html`${elements}`;
	}

	/**
	 * Draws out elements for the "subfooter" (not used in the Your Store Card)
	 * @return {html}
	 */
	drawSubfooter () {
		return html`
			<div class="location-card__todays-hours">${this.todaysHours()}</div>
			<button class="location-card__details-button g-button--no-styling" @click="${this.toggleDetails}" aria-expanded="${this.showDetails}" aria-controls="location-card-details-${this.uniqueId}">
				<span class="location-card__details-button-text">Store Details</span>
				<span class="location-card__details-button-icon u-icon-chevron-down" aria-hidden="true"></span>
			</button>
		`;
	}

	/**
	 * Draws out elements for the "footer"
	 * @return {html}
	 */
	drawFooter () {
		const elements = [];

		if (!this.isMyStore && this.type === 'mystore') {
			elements.push(this.drawShopStoreSearch());
		} else if (!this.isMyStore && this.isProductOrBasket && !this.isCurrentlySelected) {
			elements.push(this.drawShopStoreProduct());
		} else if (!this.isMyStore && this.type === 'search' && !this.isMyStore) {
			elements.push(this.drawShopStoreSearch());
		}

		if (elements.length) {
			return html`<div class="location-card__footer">${elements}</div>`;
		}

		return nothing;
	}

	/**
	 * Draws out elements for the location details (hidden initially on load)
	 * @return {html}
	 */
	drawLocationDetails () {
		return html`
			<div id="location-card-details-${this.uniqueId}" class="location-card__details" aria-hidden="${!this.showDetails}">
				<div class="location-card__hours-title">
					Store Hours
				</div>
				<div class="location-card__details-content">
					<div class="location-card__details-left">
						${this.drawHours()}
					</div>
					<div class="location-card__details-right">
						<div class="location-card__phone">${this.store.properties.phone}</div>
						<div class="location-card__details-address">${this.formattedAddress(true)}</div>
					</div>
				</div>
			</div>
		`;
	}

	/**
	 * Draws out the elements for the store hours
	 * @return {html}
	 */
	drawHours () {
		const formattedHours = this.store.properties?.formattedHours;
		if (!formattedHours) {
			return nothing;
		}

		const days = [
			'Mon',
			'Tue',
			'Wed',
			'Thu',
			'Fri',
			'Sat',
			'Sun'
		];

		// Find today's day key
		const todayKey = days.find(day => formattedHours?.[day]?.isToday);

		// Rotate days so today is first
		const startIndex = todayKey ? days.indexOf(todayKey) : 0;
		const orderedDays = [
			...days.slice(startIndex),
			...days.slice(0, startIndex)
		];

		// Render in correct order
		return html`${orderedDays.map(day => this.drawHour(day))}`;
	}

	/**
	 * Draws out the element for each day with it's hours.
	 * @return {html}
	 */
	drawHour (day) {
		const hour = this.store.properties?.formattedHours?.[day];

		if (!hour) {
			return nothing;
		}

		return html`
			<div class="location-card__store-hour${hour.isToday ? ' location-card__store-hour--today' : ''}">
				<div class="location-card__store-hour-day">${day}:</div>
				<div class="location-card__store-hour-time">${hour.formattedOpen} - ${hour.formattedClose}</div>
			</div>
		`;
	}

	/**
	 * Formats the Uberall Address:
	 * 	Address Line 1
	 * 	City, State Zip
	 * @return {html}
	 */
	formattedAddress (breakTag = true) {
		let formattedAddress = [html`${this.store.properties.address}`];
		formattedAddress.push(breakTag ? html`<br>` : html`, `);

		if (this.store.properties.city) {
			formattedAddress.push(html`${this.store.properties.city}`);
		}

		if (this.store.properties.state) {
			if (this.store.properties.city) {
				formattedAddress.push(html`, `);
			}

			formattedAddress.push(html`${this.store.properties.state}`);
		}

		if (this.store.properties.zip) {
			if (this.store.properties.state) {
				formattedAddress.push(html` `);
			}

			formattedAddress.push(html`${this.store.properties.zip}`);
		}

		return html`${formattedAddress}`;
	}

	/**
	 * Toggles `showDetails`
	 */
	toggleDetails () {
		this.showDetails = !this.showDetails;
	}

	/**
	 * Draws the "Set As My Default Store" Toggle to set the store as your "My Store"
	 * @return {html}
	 */
	drawSetAsMyStoreButton () {
		return html`<x-my-store-toggle .store="${this.store}"></x-my-store-toggle>`;
	}

	/**
	 * Draws the "Shop Store" Button. Will emit an event of the store selected. (`type` = `product`)
	 * @return {html}
	 */
	drawShopStoreProduct () {
		if (this.inStoreOnly || this.excludeBopis) {
			return nothing;
		}

		if (this.excludeBopis) {
			return nothing;
		}

		if (!this.isProductOrBasket) {
			return nothing;
		}

		return html`
			<button class="g-button-secondary g-button-width--regular" data-action="shop-store-product" @click="${this.shopStoreProduct}" ?disabled="${!this.store.productInventory}">Pick Up Here</button>
		`;
	}

	/**
	 * Draws the "Shop Store" Button. Will emit an event of the store selected. (`type` = `search`)
	 * @return {html}
	 */
	drawShopStoreSearch () {
		return html`
			<button class="g-button-secondary" data-action="shop-store-product" @click="${this.shopStoreSearch}" aria-label="Select ${this.store.name} As Your Store">Select This Store</button>
		`;
	}

	/**
	 * Draws the Inventory Flag if applicable
	 * @return {html}
	 */
	drawProductInventoryFlag () {
		if (!this.isProductOrBasket) {
			return nothing;
		}

		if (!this.store.productInventory) {
			return html`
				<div class="location-card-flag-outer location-card-flag--inventory">
					<span class="location-card-flag location-card-flag--unavailable">Unavailable</span>
				</div>
			`;
		}

		if (this.store.productInventory > 5) {
			return html`
				<div class="location-card-flag-outer location-card-flag--inventory">
					<span class="location-card-flag location-card-flag--instock">In Stock</span>
				</div>
			`;
		}

		return html`
			<div class="location-card-flag-outer location-card-flag--inventory">
				<span class="location-card-flag location-card-flag--low">Only ${this.store.productInventory} Left</span>
			</div>
		`;
	}

	drawCurrentlySelectedFlag () {
		return html`
			<div class="location-card-flag-outer">
				<span class="location-card-flag location-card-flag--selected">Current Pick Up</span>
			</div>
		`;
	}

	/**
	 * Formats today's hours & outputs "Today"'s hours for the location
	 * @return {String}
	 */
	todaysHours () {
		const todaysHours = this.store.properties?.formattedHours?.[this.controller.api.dayOfWeek];

		if (!todaysHours) {
			return nothing;
		}

		return `Today's Hours: ${todaysHours.formattedOpen} - ${todaysHours.formattedClose}`;
	}

	/**
	 * Sets the current location as "My Store"
	 */
	setLocationAsMyStore (e) {
		const location = e.target?.checked ? this.store : {};

		this.controller.setMyStore(location);

		EventBus.dispatchEvent('MVPS:myStoreChanged', {
			location: location
		});
	}

	/**
	 * Sets the current location as "My Store" & emits an event of the store picked - For `type` = `product`
	 */
	shopStoreProduct () {
		if (this.type === 'basket') {
			this.shopStoreBasket();
			return;
		}

		this.controller.setMyStore(this.store);
		EventBus.dispatchEvent('MVPS:shopStoreProductChanged', {
			location: this.store
		});
	}

	/**
	 * Sets the current location as "My Store" & emits an event of the store picked - For `type` = `search`
	 */
	shopStoreSearch () {
		this.controller.setMyStore(this.store);

		EventBus.dispatchEvent('MVPS:shopStoreSearchChanged', {
			location: this.store
		});
	}

	/**
	 * Sets the current location as "My Store" & emits an event of the store picked - For `type` = `basket`
	 */
	shopStoreBasket () {
		this.basketBopisStore = this.store;

		EventBus.dispatchEvent('MVPS:shopStoreBasketChanged', {
			basketgroup: this.basketgroup,
			location: this.store
		});
	}

	/**
	 * Formats the location distance as `0.00 Miles` (originally in Meters)
	 * @return {String}
	 */
	getDistance () {
		if (!this.showRadius || !this.store.milesAway) {
			return nothing;
		}

		return `${this.store.milesAway} ${this.store.milesAway === 1.0 ? 'Mile' : 'Miles'}`;
	}

	/**
	 * Slugify a string
	 * @param {String} text
	 * @returns {void}
	 */
	slugify (text) {
		return text?.toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-');
	}

	get isProductOrBasket () {
		return this.type === 'product' || this.type === 'basket';
	}

};

customElements.define('x-location-card', LocationCard);
